import { z } from 'zod';
import { roleSchema } from './roles';

/**
 * Payment + deposit shapes (M4). Mirrors the `payments` / `deposits` tables in
 * `docs/03-data-model.md` and the endpoints in `docs/04-api-contract.md`. Money is
 * integer minor units (kuruş); timestamps are epoch-ms integers on the wire.
 *
 * Card fields are **request-only** and never persisted — the PAN/CVV go to the bank
 * and are dropped; only a masked PAN (first 6 + last 4) and the holder name are stored
 * (see `apps/api/src/lib/payment/orders.ts`). Payments run through a `PaymentService`
 * interface (ADR 0011); NestPay is the first implementation.
 */

/** Payment kind. `deposit` gates bidding; `GOLDBIDDER` grants the membership role. */
export const paymentKindSchema = z.enum(['deposit', 'GOLDBIDDER']);
export type PaymentKind = z.infer<typeof paymentKindSchema>;

/** Payment lifecycle (`payments.state`): WAITING → PRE3D → SUCCESSFUL | FAIL; VOIDED/REFUNDED are P1. */
export const paymentStateSchema = z.enum([
  'WAITING',
  'PRE3D',
  'SUCCESSFUL',
  'FAIL',
  'VOIDED',
  'REFUNDED',
]);
export type PaymentState = z.infer<typeof paymentStateSchema>;

/** Deposit lifecycle (`deposits.state`): PENDING → HELD; CAPTURED/REFUNDED/VOIDED are later. */
export const depositStateSchema = z.enum([
  'PENDING',
  'HELD',
  'CAPTURED',
  'REFUNDED',
  'VOIDED',
]);
export type DepositState = z.infer<typeof depositStateSchema>;

// --- Request DTOs -----------------------------------------------------------

/**
 * Card details for a 3DS initiation — **request-only, never stored or logged**. The
 * server forwards them to the bank and keeps only the masked PAN + holder. Validation
 * is shape-only (Luhn is the bank's job): 12–19 PAN digits, `MM/YY` or `MMYY` expiry,
 * 3–4 digit CVV.
 */
export const cardSchema = z.object({
  holder: z.string().min(1).max(100),
  number: z.string().regex(/^\d{12,19}$/, 'card number must be 12–19 digits'),
  expiry: z.string().regex(/^(0[1-9]|1[0-2])\/?\d{2}$/, 'expiry must be MM/YY or MMYY'),
  cvv: z.string().regex(/^\d{3,4}$/, 'cvv must be 3–4 digits'),
});
export type CardInput = z.infer<typeof cardSchema>;

/**
 * `POST /sales/:saleId/lots/:lotId/deposits` — initiate a 3DS deposit. The Turnstile
 * token guards the endpoint against bots (verified server-side, ADR-cross-cutting).
 */
export const depositInitiateSchema = z.object({
  card: cardSchema,
  turnstileToken: z.string().min(1),
});
export type DepositInitiateInput = z.infer<typeof depositInitiateSchema>;

/**
 * `POST /memberships/purchase` — initiate a 3DS membership purchase. `membershipId` picks
 * an active tier from the admin catalog (`/admin/config/memberships`); the amount + granted
 * role come from that tier, so the client never sends a price.
 */
export const membershipPurchaseSchema = z.object({
  membershipId: z.string().min(1),
  card: cardSchema,
  turnstileToken: z.string().min(1),
});
export type MembershipPurchaseInput = z.infer<typeof membershipPurchaseSchema>;

// --- Admin config: global payment amounts (kuruş) ---------------------------

/**
 * Effective global single-value payment amounts (kuruş), read/managed by admins. Backed
 * by KV `CONFIG` with code defaults (`apps/api/src/lib/payment/amounts.ts`); the admin UI
 * (M6) reads/writes these via `/admin/config/payment-amounts`. Membership prices are NOT
 * here — those live in the configurable membership catalog below.
 */
export const paymentAmountsSchema = z.object({
  deposit: z.number().int().positive(), // deposit amount
});
export type PaymentAmounts = z.infer<typeof paymentAmountsSchema>;

/** Partial update — at least one amount, each a positive integer kuruş. */
export const paymentAmountsUpdateSchema = paymentAmountsSchema
  .partial()
  .refine((o) => o.deposit !== undefined, {
    message: 'at least one amount must be provided',
  });
export type PaymentAmountsUpdate = z.infer<typeof paymentAmountsUpdateSchema>;

// --- Admin config: membership catalog (names + values, configurable) --------

/**
 * One membership tier. **Fully admin-configurable** (name + price + granted role +
 * active flag) via `/admin/config/memberships`; stored in KV `CONFIG`. `id` is a stable
 * slug (referenced by the future membership-purchase route); `role` must be an existing
 * RBAC role granted on successful purchase (today typically `GOLDBIDDER`).
 */
export const membershipPackageSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'id must be a lowercase slug'),
  name: z.string().min(1).max(120), // display name (Turkish-first), e.g. "Gold Üyelik"
  amount: z.number().int().positive(), // price in kuruş
  role: roleSchema, // role granted on purchase
  active: z.boolean(),
});
export type MembershipPackage = z.infer<typeof membershipPackageSchema>;

/** The full membership catalog. Ids must be unique; an empty catalog is allowed. */
export const membershipCatalogSchema = z
  .array(membershipPackageSchema)
  .refine((pkgs) => new Set(pkgs.map((p) => p.id)).size === pkgs.length, {
    message: 'membership ids must be unique',
  });
export type MembershipCatalog = z.infer<typeof membershipCatalogSchema>;

// --- Read projections -------------------------------------------------------

/** Payment projection (admin views / "my payments"). Never carries card material. */
export const paymentSchema = z.object({
  id: z.uuid(),
  orderId: z.string(),
  userId: z.uuid(),
  saleId: z.uuid().nullable(),
  lotId: z.uuid().nullable(),
  kind: paymentKindSchema,
  grantedRole: roleSchema.nullable(), // membership: role granted on success (null for deposits)
  amount: z.number().int(), // kuruş
  currency: z.string(),
  cardHolder: z.string().nullable(),
  cardPanMasked: z.string().nullable(), // first 6 + last 4 only; never the full PAN
  state: paymentStateSchema,
  createdAt: z.number().int(), // epoch-ms
  finalizedAt: z.number().int().nullable(), // epoch-ms
});
export type Payment = z.infer<typeof paymentSchema>;

/** Deposit projection (per lot/user). A `HELD` deposit makes a plain user bid-eligible. */
export const depositSchema = z.object({
  id: z.uuid(),
  saleId: z.uuid(),
  lotId: z.uuid(),
  userId: z.uuid(),
  paymentId: z.uuid(),
  amount: z.number().int(), // kuruş
  state: depositStateSchema,
  createdAt: z.number().int(), // epoch-ms
});
export type Deposit = z.infer<typeof depositSchema>;
